import { SESSION_OPEN_TIMEFRAMES } from '../../lib/chart/chartMargins.js'
import PropTypes from 'prop-types'
import NumberInput from '../NumberInput.jsx'
import DynamicCanvasSchedule from './DynamicCanvasSchedule.jsx'
import { DYNAMIC_CANVAS_TIMEFRAMES } from '../../lib/chart/dynamicCanvas.js'

function NumberSettingInput({ value, suffix, min, max, onChange, testId, ariaLabel }) {
  return (
    <div className="flex h-7 items-center rounded-md border border-border bg-canvas2 px-2.5">
      <NumberInput
        key={ariaLabel}
        min={min}
        max={max}
        step="1"
        value={value}
        aria-label={ariaLabel}
        data-testid={testId}
        onValueChange={onChange}
        className="min-w-0 flex-1 bg-transparent text-xs tabular-nums text-ink outline-none"
      />
      <span className="ml-1 text-xs text-inkMuted">{suffix}</span>
    </div>
  )
}

NumberSettingInput.propTypes = {
  value: PropTypes.number.isRequired,
  suffix: PropTypes.string.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
}

export default function ChartCanvasMargins({ activeTimeframe, activeTimeframeLabel, margins, onMarginsChange, sessionOpenViewEnabled = false }) {
  const dynamicEnabled = sessionOpenViewEnabled && margins.defaultView === 'dynamic'
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-inkMuted">Margins</div>
        <div
          className="rounded border border-border bg-canvas2 px-2 py-0.5 text-[10px] font-semibold text-ink"
          data-testid="chart-settings-margin-timeframe"
        >
          {activeTimeframeLabel}
        </div>
      </div>
      <div className="grid grid-cols-[112px_1fr] items-center gap-3">
        {sessionOpenViewEnabled && SESSION_OPEN_TIMEFRAMES.includes(activeTimeframe) && <>
          <div className="text-xs text-inkMuted">Default view</div>
          <select aria-label={`${activeTimeframe} default view`} data-testid="chart-settings-default-view"
            className="h-7 rounded-md border border-border bg-canvas2 px-2 text-xs text-ink"
            value={margins.defaultView}
            onChange={event => onMarginsChange({ defaultView: event.target.value })}>
            {DYNAMIC_CANVAS_TIMEFRAMES.includes(activeTimeframe) && <option value="dynamic">Dynamic schedule</option>}
            <option value="session-open">Session open</option>
            <option value="latest">Latest candles</option>
          </select>
          {['session-open','dynamic'].includes(margins.defaultView) && <>
            <div className="text-xs text-inkMuted">Before open</div>
            <NumberSettingInput value={margins.candlesBeforeOpen} suffix="bars" min={0} max={100}
              ariaLabel={`${activeTimeframe} candles before open`} testId="chart-settings-before-open"
              onChange={value => onMarginsChange({ candlesBeforeOpen: value })} />
            {margins.defaultView === 'session-open' && <p className="col-span-2 text-xs leading-5 text-inkMuted">
              Starts before the regular-session open and keeps it in view all day.
              Applies when opening a chart, switching assets, or resetting the view.
              Visible bars sets the minimum width; the view widens as the session grows.
            </p>}
          </>}
          {margins.defaultView === 'dynamic' && <DynamicCanvasSchedule schedule={margins.dynamicSchedule} onChange={dynamicSchedule=>onMarginsChange({dynamicSchedule})} />}
        </>}
        <div className="text-xs text-inkMuted">Source</div>
        <div
          className="h-7 rounded-md border border-border bg-canvas2 px-2.5 text-xs leading-7 text-ink"
          data-testid="chart-settings-margin-source"
        >
          Price action only
        </div>
        {!dynamicEnabled && <><div className="text-xs text-inkMuted">Visible</div>
        <NumberSettingInput
          value={Math.round(margins.visibleBars || 0)}
          suffix="bars"
          min={10}
          max={2000}
          ariaLabel={`${activeTimeframe} visible bars`}
          testId="chart-settings-margin-visible"
          onChange={(value) => onMarginsChange({ visibleBars: value })}
        /></>}
        <div className="text-xs text-inkMuted">Top</div>
        <NumberSettingInput
          value={Math.round((margins.top || 0) * 100)}
          suffix="%"
          min={0}
          max={80}
          ariaLabel={`${activeTimeframe} top margin percent`}
          testId="chart-settings-margin-top"
          onChange={(value) => onMarginsChange({ topPercent: value })}
        />
        <div className="text-xs text-inkMuted">Bottom</div>
        <NumberSettingInput
          value={Math.round((margins.bottom || 0) * 100)}
          suffix="%"
          min={0}
          max={80}
          ariaLabel={`${activeTimeframe} bottom margin percent`}
          testId="chart-settings-margin-bottom"
          onChange={(value) => onMarginsChange({ bottomPercent: value })}
        />
        {!dynamicEnabled && <><div className="text-xs text-inkMuted">Right</div>
        <NumberSettingInput
          value={Math.round(margins.rightOffset || 0)}
          suffix="bars"
          min={0}
          max={300}
          ariaLabel={`${activeTimeframe} right margin bars`}
          testId="chart-settings-margin-right"
          onChange={(value) => onMarginsChange({ rightOffset: value })}
        /></>}
      </div>
    </section>
  )
}
